import React, { useState, useEffect } from 'react';
import { Modal, Alert, ListGroup } from 'react-bootstrap';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { CreditCard, Plus } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import { API_ENDPOINTS, STRIPE_PUBLISHABLE_KEY } from '../../../config/api.config';
import { PaymentMethodForm } from "../../billing";
import { Payment, PaymentMethodResponse } from '../../../types/api';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface Plan {
    id: string;
    name: string;
    price: number;
    billing_interval: 'monthly' | 'yearly';
}

interface Subscription {
    id: string;
    current_period_end: string;
    plan: Plan;
}

interface SubscriptionUpgradeModalProps {
    show: boolean;
    onHide: () => void;
    currentSubscription: Subscription;
    selectedPlan: Plan;
    onUpgradeComplete: () => void;
}

export const SubscriptionUpgradeModal: React.FC<SubscriptionUpgradeModalProps> = ({
                                                                                      show,
                                                                                      onHide,
                                                                                      currentSubscription,
                                                                                      selectedPlan,
                                                                                      onUpgradeComplete,
                                                                                  }) => {
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
    const [paymentMethods, setPaymentMethods] = useState<Payment[]>([]);
    const [showAddPayment, setShowAddPayment] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);

    useEffect(() => {
        const initStripe = async () => {
            const stripe = await stripePromise;
            setStripeInstance(stripe);
        };
        initStripe();
    }, []);

    useEffect(() => {
        if (show) {
            fetchPaymentMethods();
        }
    }, [show]);

    const fetchPaymentMethods = async () => {
        try {
            const response = await apiService.get<PaymentMethodResponse>(API_ENDPOINTS.BILLING.PAYMENT_METHODS.LIST);
            if (response?.data) {
                setPaymentMethods(response.data);
                if (response.data.length > 0) {
                    setSelectedPaymentMethod(response.data[0].id);
                }
            }
        } catch (err) {
            setError('Failed to load payment methods');
            console.error(err);
        }
    };

    const calculateProration = () => {
        const daysRemaining = Math.ceil(
            (new Date(currentSubscription.current_period_end).getTime() - new Date().getTime()) /
            (1000 * 3600 * 24)
        );

        const prorationCredit = (currentSubscription.plan.price / 30) * daysRemaining;
        const newPlanCharge = (selectedPlan.price / 30) * daysRemaining;
        const totalDue = Math.max(0, newPlanCharge - prorationCredit);

        return {
            prorationCredit,
            newPlanCharge,
            totalDue,
            daysRemaining
        };
    };

    const handlePaymentMethodAdded = async (paymentMethodId: string) => {
        try {
            await apiService.post<{ message: string }>(API_ENDPOINTS.BILLING.PAYMENT_METHODS.ADD, {
                paymentMethodId
            });
            setShowAddPayment(false);
            await fetchPaymentMethods();
            setSelectedPaymentMethod(paymentMethodId);
        } catch (err) {
            console.error('Error adding payment method:', err);
            setError(err instanceof Error ? err.message : 'Failed to add payment method');
        }
    };

    const handleUpgrade = async () => {
        if (!stripeInstance || !selectedPaymentMethod) {
            setError('Please select a payment method');
            return;
        }

        setPaymentLoading(true);
        try {
            const result = await apiService.post<{data: { clientSecret?: string }}>(
                API_ENDPOINTS.BILLING.SUBSCRIPTION.UPGRADE,
                {
                    planId: selectedPlan.id,
                    paymentMethodId: selectedPaymentMethod
                }
            );

            if (result?.data?.clientSecret) {
                const { error: paymentError } = await stripeInstance.confirmCardPayment(
                    result.data.clientSecret,
                    {
                        payment_method: selectedPaymentMethod
                    }
                );

                if (paymentError) {
                    setError(`Payment failed: ${paymentError.message}`);
                    return;
                }

                onUpgradeComplete();
                handleClose();
            }
        } catch (err) {
            console.error('Upgrade error:', err);
            setError(err instanceof Error ? err.message : 'Failed to upgrade plan');
        } finally {
            setPaymentLoading(false);
        }
    };

    const handleClose = () => {
        setError(null);
        setShowAddPayment(false);
        onHide();
    };

    const { prorationCredit, newPlanCharge, totalDue } = calculateProration();

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Upgrade to {selectedPlan.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-4">
                        {error}
                    </Alert>
                )}

                <div className="mb-4">
                    <h6>Payment Summary</h6>
                    <ListGroup variant="flush">
                        <ListGroup.Item className="d-flex justify-content-between">
                            <span>Credit for remaining days:</span>
                            <span>${prorationCredit.toFixed(2)}</span>
                        </ListGroup.Item>
                        <ListGroup.Item className="d-flex justify-content-between">
                            <span>New plan prorated charge:</span>
                            <span>${newPlanCharge.toFixed(2)}</span>
                        </ListGroup.Item>
                        <ListGroup.Item className="d-flex justify-content-between fw-bold">
                            <span>Total due now:</span>
                            <span>${totalDue.toFixed(2)}</span>
                        </ListGroup.Item>
                    </ListGroup>
                </div>

                {showAddPayment ? (
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Add New Payment Method</h6>
                            <button
                                className="btn btn-link p-0"
                                onClick={() => setShowAddPayment(false)}
                            >
                                Back to saved cards
                            </button>
                        </div>
                        <Elements stripe={stripePromise}>
                            <PaymentMethodForm
                                onSuccess={handlePaymentMethodAdded}
                                buttonText="Add Payment Method"
                            />
                        </Elements>
                    </div>
                ) : (
                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6 className="mb-0">Select Payment Method</h6>
                            <button
                                className="btn btn-link p-0 d-flex align-items-center"
                                onClick={() => setShowAddPayment(true)}
                            >
                                <Plus size={16} className="me-1" />
                                Add New
                            </button>
                        </div>

                        {paymentMethods.length === 0 ? (
                            <Alert variant="info">
                                No payment methods available. Please add one to continue.
                            </Alert>
                        ) : (
                            <ListGroup>
                                {paymentMethods.map((method) => (
                                    <ListGroup.Item
                                        key={method.id}
                                        action
                                        active={selectedPaymentMethod === method.id}
                                        onClick={() => setSelectedPaymentMethod(method.id)}
                                        className="d-flex align-items-center"
                                    >
                                        <input
                                            type="radio"
                                            name="paymentMethod"
                                            checked={selectedPaymentMethod === method.id}
                                            onChange={() => setSelectedPaymentMethod(method.id)}
                                            className="me-3"
                                        />
                                        <CreditCard size={20} className="me-2" />
                                        <div>
                                            <span className="text-capitalize">{method.card.brand}</span>
                                            <span className="ms-2">•••• {method.card.last4}</span>
                                            <span className="ms-2 text-muted">
                                                Expires {method.card.exp_month}/{method.card.exp_year}
                                            </span>
                                            {method.isDefault && (
                                                <span className="badge bg-secondary ms-2">Default</span>
                                            )}
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        )}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button
                    className="btn btn-secondary"
                    onClick={handleClose}
                    disabled={paymentLoading}
                >
                    Cancel
                </button>
                <button
                    className="btn btn-primary"
                    onClick={handleUpgrade}
                    disabled={paymentLoading || !selectedPaymentMethod || showAddPayment}
                >
                    {paymentLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                            Processing Payment...
                        </>
                    ) : (
                        `Pay ${totalDue.toFixed(2)} & Upgrade`
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};